/**
 * Data mode — ADR-0073 §1 (V105-2-1), Settings → Security #data-mode.
 *
 * GET  /settings/data-mode  any member.
 * PUT  /settings/data-mode  owner only: standard → federal with the slug typed to
 *      confirm; there is no way back (409 on downgrade). Others get AskAdmin.
 *
 * v10.5 federal mode partitions AI calls; it does not move storage. The one
 * scope line below says so (ADR-0073 §1: no residency overclaim).
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Field, Input, Spinner } from '@aigovops/design-system';
import { getAiPolicy, getDataMode, putDataMode } from '../lib/api';
import { queryKeys } from '../lib/query-keys';
import { errorText } from './today/shared';
import { AskAdmin } from './AskAdmin';
import './data-protection-settings.css';

export function DataModeSettings({ slug, role }: { slug: string; role: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: queryKeys.dataMode(slug), queryFn: () => getDataMode(slug), retry: false, staleTime: 60_000 });
  const policy = useQuery({ queryKey: queryKeys.aiPolicy(slug), queryFn: () => getAiPolicy(slug), retry: false, staleTime: 60_000 });
  const owner = role === 'owner';
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  const mode = q.data?.data_mode;
  const federal = mode?.data_mode === 'federal';
  const legacyFlag = !!policy.data?.policy.federal_boundary_only && !federal;

  const start = () => { setConfirming(true); setErr(null); setTyped(''); window.setTimeout(() => document.getElementById('dps-confirm-slug')?.focus(), 0); };
  const confirm = async () => {
    if (busy) return;
    if (typed.trim() !== slug) { setErr(`Type ${slug} to confirm.`); return; }
    setBusy(true); setErr(null);
    try {
      const r = await putDataMode(slug, { data_mode: 'federal', confirm_slug: typed.trim() });
      qc.setQueryData(queryKeys.dataMode(slug), { data_mode: r.data_mode });
      void qc.invalidateQueries({ queryKey: ['federal-parity', slug] });
      setConfirming(false);
      setMsg('Federal mode is on');
    } catch (e) { setErr(errorText(e, 'Federal mode did not turn on.')); }
    finally { setBusy(false); }
  };

  return (
    <div id="data-mode" className="dps" tabIndex={-1} role="region" aria-labelledby="dps-mode-h">
      <Card>
        <div className="dps-body">
          <h2 id="dps-mode-h" className="dps-h">Data mode</h2>
          {q.isLoading ? <Spinner label="Loading data mode" /> : q.error || !mode ? (
            <Alert variant="error"><p>Data mode could not load.</p><Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>
          ) : (
            <>
              <div className="dps-row">
                <Badge tone={federal ? 'accent' : 'neutral'}>{federal ? 'Federal' : 'Standard'}</Badge>
                {mode.locked_by_org ? <span className="dps-dim">Set by your organization</span> : null}
                {federal && mode.data_mode_set_at ? <span className="dps-dim">Since {mode.data_mode_set_at.slice(0, 10)}</span> : null}
              </div>
              <p className="dps-dim">Federal: AI calls with your data use only FedRAMP-authorized or your own providers. Storage does not move.</p>
              {legacyFlag ? (
                <Alert variant="warning">
                  <p>In-boundary only is on. Federal mode replaces it.</p>
                  {owner && !confirming ? <Button variant="secondary" compact onClick={start}>Turn on federal mode</Button> : null}
                </Alert>
              ) : null}
              {!federal && owner && !confirming && !legacyFlag ? (
                <div className="dps-row"><Button variant="secondary" compact onClick={start}>Turn on federal mode</Button></div>
              ) : null}
              {!federal && owner && confirming ? (
                <div className="dps-confirm">
                  <Field id="dps-confirm-slug" label={`Type ${slug} to confirm. Federal mode can't be turned off.`}>
                    <Input value={typed} autoComplete="off" spellCheck={false} aria-invalid={err ? true : undefined}
                      aria-describedby={err ? 'dps-mode-err' : undefined}
                      onChange={(e) => { setTyped(e.currentTarget.value); setErr(null); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') void confirm(); }} />
                  </Field>
                  {err ? <p id="dps-mode-err" className="dps-err" role="alert">{err}</p> : null}
                  <div className="dps-row">
                    <Button variant="danger" compact aria-disabled={busy || undefined} onClick={() => void confirm()}>{busy ? 'Turning on…' : 'Turn on federal mode'}</Button>
                    <Button variant="ghost" compact onClick={() => { setConfirming(false); setErr(null); }}>Cancel</Button>
                  </div>
                </div>
              ) : null}
              {!federal && !owner ? (
                <AskAdmin slug={slug} single roles={['owner']} lead="A workspace owner" what="turns on federal mode" subject="Turn on federal mode for this workspace" />
              ) : null}
            </>
          )}
          <p role="status" className="sr-only">{msg}</p>
        </div>
      </Card>
    </div>
  );
}
